import type { AuthMethod } from '@shared/auth';
import { create } from 'zustand';

export type AuthState = 'loading' | 'unauthenticated' | 'authenticated';
export type AuthModalTab = 'login' | 'signup';

interface AuthUser {
  userId: string;
  nickname: string | null;
  email: string | null;
  authMethod: AuthMethod;
  tierId: number;
  /**
   * Lowercase canonical tier identifier ('guest' | 'regular' | 'premium' |
   * 'vip' | 'admin'). Sourced from `/api/v1/auth/me` (JOIN subscription_tiers).
   * Prefer this over `tierId` for any tier-conditioned UI — frontend MUST NOT
   * map `tierId` integer locally (see B08 Guest-not-blocked bug 2026-05-17).
   */
  tierKey: string;
  ageVerificationStatus: string;
}

interface AuthStore {
  authState: AuthState;
  user: AuthUser | null;
  csrfToken: string;
  authModalOpen: boolean;
  authModalTab: AuthModalTab;

  setUser: (user: AuthUser, csrf: string) => void;
  clearAuth: () => void;
  setLoading: () => void;
  openAuthModal: (tab?: AuthModalTab) => void;
  closeAuthModal: () => void;
  switchTab: (tab: AuthModalTab) => void;
}

export const useAuthStore = create<AuthStore>((set) => ({
  authState: 'loading',
  user: null,
  csrfToken: '',
  authModalOpen: false,
  authModalTab: 'login',

  setUser: (user, csrf) =>
    set({ authState: 'authenticated', user, csrfToken: csrf, authModalOpen: false }),

  clearAuth: () =>
    set({ authState: 'unauthenticated', user: null, csrfToken: '' }),

  setLoading: () => set({ authState: 'loading' }),

  openAuthModal: (tab = 'login') =>
    set({ authModalOpen: true, authModalTab: tab }),

  closeAuthModal: () => set({ authModalOpen: false }),

  switchTab: (tab) => set({ authModalTab: tab }),
}));
